import {useEffect, useMemo, useState} from "react";
import hash from "object-hash";
import {DefaultButton, Label, ProgressIndicator} from "@fluentui/react";
import {TenantResource} from "../enums/TenantResource";
import {useTenantResourceListParametrized,} from "../hooks/useTenantResourceList";
import {TenantForm} from "./TenantForm";
import {ITenantFormLayout} from "./OnixElements";
import {ITenantResourceQuery} from "../interfaces/ITenantResourceQuery";
import {FormAction, TWithStoragesAndOriginals} from "../types/TenantTypes";
import {GetApiError, ShowApiError} from "./ShowApiError";

interface ISingleTenantItemForm<TDatabaseModel, TDto, TReturnDto> {
    resource: TenantResource;
    form: ITenantFormLayout<TDto>;
    baseDto: TWithStoragesAndOriginals<Partial<TDto>>;
    id?: string;
    getId?: (dto: TDto) => string | undefined;
    query?: ITenantResourceQuery<TReturnDto>;
    style?: React.CSSProperties;
    afterFormAction?: (formAction: FormAction, dto: TReturnDto) => void;
}

export const SingleTenantItemForm = <TDatabaseModel, TDto, TReturnDto>({
                                                                           resource,
                                                                           afterFormAction,
                                                                           form,
                                                                           baseDto,
                                                                           id,
                                                                           getId,
                                                                           query,
                                                                           style,
                                                                       }: ISingleTenantItemForm<TDatabaseModel, TDto, TReturnDto>) => {
    const tableIdentifier = useMemo<string>(
        () =>
            hash({
                form,
                baseDto,
                resource,
                id,
            }),
        [form, id, baseDto, resource],
    );

    const [error, setError] = useState<string>();

    const {selectedItem, reload, setQuery, setSelectedItem, result, error: loadError, loading} =
        useTenantResourceListParametrized<TDatabaseModel, TDto, TReturnDto>({
            resource,
            cacheKey: tableIdentifier,
            initialQuery: query,
            queryIsRequired: !!query,
            getId,
            paginate: false,
            afterFormAction,
        });

    // resource,
    //     tableIdentifier,
    //     query,
    //     true,
    //     false,
    //     undefined,
    //     undefined,
    //     undefined,
    //     getId,
    //     undefined,
    //     undefined

    useEffect(() => {
        if (query) {
            setQuery(query);
        }
    }, [query]);

    useEffect(() => {
        if (result) {
            if (result.totalItems > 0) {
                setSelectedItem(result.items[0]);
            } else {
                if (query) {
                    setError("No items found");
                }
                setSelectedItem(undefined);
            }
        }
    }, [result, query]);

    // Only an initial required read blocks this form. A later refresh failure
    // must not unmount an existing draft, including forms without a query.
    if (loadError && query && !selectedItem) {
        return <div style={style}>
            <ShowApiError errors={{"": [GetApiError(loadError, "") || "Podatke nije moguće učitati. Pokušajte ponovno."]}} keys={[""]}/>
            <DefaultButton text="Pokušaj ponovno" disabled={loading} onClick={() => {
                if (query) setQuery(query);
            }}/>
        </div>;
    }

    if (error) {
        return <Label>{error}</Label>;
    }

    if (selectedItem || !query) {
        return (
            <TenantForm
                key={`${resource}-${tableIdentifier}`}
                style={style}
                baseDto={baseDto}
                baseModel={selectedItem}
                form={form}
                reload={reload}
                resource={resource}
                getId={getId}
                inline
                afterFormAction={afterFormAction}
            />
        );
    }

    return <ProgressIndicator/>;
};
